import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { MetasPlanCompetencia } from "./metas-plan-competencia.service";

/**
 * 🔹 Payload para crear/actualizar
 */
export interface PlanCompetenciaAnualPayload {
  objetivo: string;
  fecha_inicio: string;
  fecha_fin: string;
  metas: string[];
  estado?: number;
}
/**
 * 🔹 Interface de respuesta
 */
export interface PlanCompetenciaAnual {
  id?: number;
  objetivo: string;
  fecha_inicio: string;
  fecha_fin: string;
  estado: number;
  metas?: MetasPlanCompetencia[];
  fechaCreacion?: string;
  fechaModificacion?: string | null;
}

export interface PlanCompetenciaAnualResponse {
  id?: number;
  message?: string;
  error?: string;
  data?: any;
  [key: string]: any;
}

/**
 * 🔹 Ejecución de una actividad de capacitación (registro individual de asistencia/realización)
 */
export interface CronogramaEjecucion {
  id: number;
  fechaCreacion: string;
  [key: string]: any;
}

/**
 * 🔹 Tema/subactividad dentro de una actividad (CapacitacionActividadRe)
 */
export interface CronogramaActividadRe {
  id: number;
  id_actividad: number;
  actividad: string;
  estado: number;
}

/**
 * 🔹 Actividad de capacitación (CapacitacionActividad), con sus temas y ejecuciones.
 * Las ejecuciones y el mes_programado viven a nivel de ACTIVIDAD, no a nivel de cada tema (actividadre).
 */
export interface CronogramaActividad {
  id: number;
  id_capacitacion: number;
  actividad: string;
  dirigido: string;
  dirigido_id?: number | null; // 👈 NUEVO: id del cargo (CapacitacionActividad.dirigido), usado en cobertura por cargo
  responsable: string;
  fecha_programado: string | null;
  mes_programado: number | null;
  actividadre: CronogramaActividadRe[];
  ejecuciones: CronogramaEjecucion[];
}

/**
 * 🔹 Asistencia individual a una programación (Indicador 12 — CPF PESV)
 */
export interface Asistente {
  cedula: number | string;
  asistio: number;
}

/**
 * 🔹 Programación (CapacitacionProgramacion) con su capacitación y actividades
 */
export interface CronogramaProgramacion {
  asistentes: Asistente[];
  id: number;
  capacitacion_id: number | null;
  capacitacion: { id: number; nombre: string } | null;
  fecha_inicio: string;
  fecha_final: string;
  modalidad?: string;
  lugar?: string;
  url?: string;
  instructor?: string;
  observaciones?: string;
  estado: number;
  actividades: CronogramaActividad[];
}

/**
 * 🔹 Meta del plan de competencia, tal como la devuelve el cronograma
 * (incluye la info del plan/competencia al que pertenece)
 */
export interface CronogramaMeta {
  id: number;
  id_competencia: number;
  metas: string;
  competencia: {
    id: number;
    objetivo: string;
    fecha_inicio: string;
    fecha_fin: string;
    estado: number;
  };
}

/**
 * 🔹 Resumen mensual (programadas vs ejecutadas) para la hoja de indicadores
 */
export interface CronogramaResumenMes {
  mes: number;
  nombre: string;
  programadas: number;
  ejecutadas: number;
}

export interface CronogramaAnualResponse {
  anio: number;
  programaciones: CronogramaProgramacion[];
  resumenMensual: CronogramaResumenMes[];
  metas: CronogramaMeta[];
}

@Injectable({
  providedIn: "root",
})
export class PlanCompetenciaAnualService {
  private readonly apiUrl = `${environment.apiUrl}/api/plan_competencia_anual`;

  constructor(private http: HttpClient) {}

  // Listar todos los planes de competencia anual
  getAll(): Observable<PlanCompetenciaAnual[]> {
    return this.http.get<PlanCompetenciaAnual[]>(this.apiUrl, { withCredentials: true });
  }

  // Obtener por ID
  getById(id: number): Observable<PlanCompetenciaAnual> {
    return this.http.get<PlanCompetenciaAnual>(`${this.apiUrl}/${id}`, { withCredentials: true });
  }

  // Crear nuevo plan de competencia anual
  create(payload: PlanCompetenciaAnualPayload): Observable<PlanCompetenciaAnualResponse> {
    return this.http.post<PlanCompetenciaAnualResponse>(this.apiUrl, payload, { withCredentials: true });
  }

  // Actualizar plan de competencia anual
  update(id: number, payload: Partial<PlanCompetenciaAnualPayload>): Observable<PlanCompetenciaAnualResponse> {
    return this.http.put<PlanCompetenciaAnualResponse>(`${this.apiUrl}/${id}`, payload, { withCredentials: true });
  }

  // Eliminar plan de competencia anual (DELETE)
  remove(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${id}`, { withCredentials: true });
  }

  // Inactivar plan de competencia anual
  inactivar(id: number): Observable<PlanCompetenciaAnualResponse> {
    return this.http.put<PlanCompetenciaAnualResponse>(`${this.apiUrl}/${id}/inactivar`, {}, { withCredentials: true });
  }

  // Activar plan de competencia anual
  activar(id: number): Observable<PlanCompetenciaAnualResponse> {
    return this.http.put<PlanCompetenciaAnualResponse>(`${this.apiUrl}/${id}/activar`, {}, { withCredentials: true });
  }

  // 🔹 Cronograma anual consolidado (programaciones + resumen mensual + metas) para exportar a Excel
  getCronograma(anio: number): Observable<CronogramaAnualResponse> {
    return this.http.get<CronogramaAnualResponse>(`${this.apiUrl}/cronograma/${anio}`, { withCredentials: true });
  }
}